import {
	BufferAttribute,
	BufferGeometry,
	Color,
	Matrix4,
	ObjectLoader,
	PerspectiveCamera,
	Points,
	PointsMaterial,
	Raycaster,
	Scene,
	WebGLRenderer
} from 'three';

export class Background extends HTMLElement {
	#camera = new PerspectiveCamera(45, globalThis.innerWidth / globalThis.innerHeight, 1, 10000);
	#loader = new ObjectLoader();
	#raycaster = new Raycaster();
	#renderer = new WebGLRenderer({ antialias: true });
	#rotateY = new Matrix4().makeRotationY(0.0023);
	#scene = new Scene();

	#hasAnimationLoop = false;
	#pointDimensions = Object.freeze({
		size: 0.05,
		cloudWidth: 80,
		cloudLength: 160,
	});

	constructor() {
		super();

		this.#camera.position.set(10, 10, 10);
		this.#camera.lookAt(this.#scene.position);
		this.#renderer.setPixelRatio(globalThis.devicePixelRatio);
		this.#renderer.setSize(globalThis.innerWidth, globalThis.innerHeight);
		this.#raycaster.params.Points.threshold = 0.1;
		this.#initializePointerEvents();
		this.appendChild(this.#renderer.domElement);
	}

	connectedCallback() {
		this.#populateScene();
		this.resize();
	}

	render() {
		this.#renderer.render(this.#scene, this.#camera);
	}

	resize() {
		this.#camera.aspect = globalThis.innerWidth / globalThis.innerHeight;
		this.#camera.updateProjectionMatrix();
		this.#renderer.setSize(globalThis.innerWidth, globalThis.innerHeight);
		if (!this.#hasAnimationLoop) this.render();
	}

	startAnimation() {
		this.#renderer.setAnimationLoop(() => {
			this.#camera.applyMatrix4(this.#rotateY);
			this.render();
		});
		this.#hasAnimationLoop = true;
	}

	stopAnimation() {
		this.#renderer.setAnimationLoop(null);
		this.#hasAnimationLoop = false;
	}

	toggleAnimation() {
		if (this.#hasAnimationLoop) {
			this.stopAnimation();
		} else {
			this.startAnimation();
		}
	}

	getState() {
		const source = this.#camera.toJSON();
		Reflect.set(source, 'zoom', this.#camera.zoom);
		return source;
	}

	/**
	 * @param {import('three').PerspectiveCameraJSON & { zoom?: number }} source
	 */
	async applyState(source) {
		this.#camera.copy(/** @type {PerspectiveCamera} */(
			await this.#loader.parseAsync(source)
		));
		this.#camera.zoom = source.zoom ?? 1;
		this.#camera.updateProjectionMatrix();
		this.render();
	}

	#initializePointerEvents() {
		let initialPinchDistance = 0;
		let lastClientX = 0;
		let shouldResume = false;
		const activePointers = new Map();

		const onPointerEnd = (/** @type {PointerEvent} */e) => {
			e.preventDefault();
			activePointers.delete(e.pointerId);
			if (activePointers.size === 0) {
				if (shouldResume) this.startAnimation();
				shouldResume = false;
				lastClientX = 0;
			}
		};

		const applyZoom = (/** @type {number} */deltaY) => {
			this.#camera.zoom -= deltaY * 0.00132;
			this.#camera.updateProjectionMatrix();
			this.render();
		};

		this.addEventListener('contextmenu', e => e.preventDefault());
		this.addEventListener('pointercancel', onPointerEnd);
		this.addEventListener('pointerup', onPointerEnd);
		this.addEventListener('pointerdown', (e) => {
			e.preventDefault();
			shouldResume = shouldResume || this.#hasAnimationLoop;
			if (this.#hasAnimationLoop) this.stopAnimation();
			activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
			if (activePointers.size === 2) {
				const [p1, p2] = [...activePointers.values()];
				initialPinchDistance = Math.hypot(p1.x - p2.x, p1.y - p2.y);
			}
		});
		this.addEventListener('pointermove', (e) => {
			const isTouch = e.pointerType === 'touch';
			if (isTouch || e.buttons > 0) {
				activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
				if (activePointers.size === 1) {
					const deltaX = e.clientX - (lastClientX || e.clientX);
					const theta = (deltaX > 0 ? -0.0138 : 0.0138) * (isTouch ? 2 : 1);
					const rotator = new Matrix4().makeRotationY(theta);
					this.#camera.applyMatrix4(rotator);
					this.render();
					lastClientX = e.clientX;
				} else {
					const [p1, p2] = [...activePointers.values()];
					const currentDistance = Math.hypot(p1.x - p2.x, p1.y - p2.y);
					applyZoom((initialPinchDistance - currentDistance) * 2);
					initialPinchDistance = currentDistance;
				}
			}
		});

		this.addEventListener('wheel', (e) => applyZoom(e.deltaY));
	}

	#populateScene() {
		this.#scene.clear();

		const { cloudWidth, cloudLength } = this.#pointDimensions;
		const pcBuffer = this.#generatePointcloud(new Color(1, 0, 0), cloudWidth, cloudLength);
		pcBuffer.scale.set(5, 10, 10);
		pcBuffer.position.set(- 5, 0, 0);

		const pcIndexed = this.#generateIndexedPointcloud(new Color(0, 1, 0), cloudWidth, cloudLength);
		pcIndexed.scale.set(5, 10, 10);
		pcIndexed.position.set(0, 0, 0);

		const pcIndexedOffset = this.#generateIndexedWithOffsetPointcloud(new Color(0, 1, 1), cloudWidth, cloudLength);
		pcIndexedOffset.scale.set(5, 10, 10);
		pcIndexedOffset.position.set(5, 0, 0);

		this.#scene.add(pcBuffer);
		this.#scene.add(pcIndexed);
		this.#scene.add(pcIndexedOffset);

		//

		this.#renderer.setPixelRatio(globalThis.devicePixelRatio);
		this.#renderer.setSize(globalThis.innerWidth, globalThis.innerHeight);

		//

		this.appendChild(this.#renderer.domElement);
		this.render();

		//

		globalThis.addEventListener('resize', () => {
			this.#camera.aspect = globalThis.innerWidth / globalThis.innerHeight;
			this.#camera.updateProjectionMatrix();
			this.#renderer.setSize(globalThis.innerWidth, globalThis.innerHeight);
			this.render();
		});
	}

	/**
	 * @param {Color} color
	 * @param {number} width
	 * @param {number} length
	 */
	#generatePointCloudGeometry(color, width, length) {
		const geometry = new BufferGeometry();
		const numPoints = width * length;

		const positions = new Float32Array(numPoints * 3);
		const colors = new Float32Array(numPoints * 3);

		let k = 0;

		for (let i = 0; i < width; i++) {
			for (let j = 0; j < length; j++) {
				const u = i / width;
				const v = j / length;
				const x = u - 0.5;
				const y = (Math.cos(u * Math.PI * 4) + Math.sin(v * Math.PI * 8)) / 20;
				const z = v - 0.5;

				positions[3 * k] = x;
				positions[3 * k + 1] = y;
				positions[3 * k + 2] = z;

				const intensity = (y + 0.1) * 5;
				colors[3 * k] = color.r * intensity;
				colors[3 * k + 1] = color.g * intensity;
				colors[3 * k + 2] = color.b * intensity;

				k++;
			}
		}

		geometry.setAttribute('position', new BufferAttribute(positions, 3));
		geometry.setAttribute('color', new BufferAttribute(colors, 3));
		geometry.computeBoundingBox();

		return geometry;
	}

	/**
	 * @param {Color} color
	 * @param {number} width
	 * @param {number} length
	 */
	#generatePointcloud(color, width, length) {
		const geometry = this.#generatePointCloudGeometry(color, width, length);
		const material = new PointsMaterial({ size: this.#pointDimensions.size, vertexColors: true });

		return new Points(geometry, material);
	}

	/**
	 * @param {Color} color
	 * @param {number} width
	 * @param {number} length
	 */
	#generateIndexedPointcloud(color, width, length) {
		const geometry = this.#generatePointCloudGeometry(color, width, length);
		const numPoints = width * length;
		const indices = new Uint16Array(numPoints);

		let k = 0;

		for (let i = 0; i < width; i++) {
			for (let j = 0; j < length; j++) {
				indices[k] = k;
				k++;
			}
		}

		geometry.setIndex(new BufferAttribute(indices, 1));

		const material = new PointsMaterial({ size: this.#pointDimensions.size, vertexColors: true });
		return new Points(geometry, material);
	}

	/**
	 * @param {Color} color
	 * @param {number} width
	 * @param {number} length
	 */
	#generateIndexedWithOffsetPointcloud(color, width, length) {
		const geometry = this.#generatePointCloudGeometry(color, width, length);
		const numPoints = width * length;
		const indices = new Uint16Array(numPoints);

		let k = 0;

		for (let i = 0; i < width; i++) {
			for (let j = 0; j < length; j++) {
				indices[k] = k;
				k++;
			}
		}

		geometry.setIndex(new BufferAttribute(indices, 1));
		geometry.addGroup(0, indices.length);

		const material = new PointsMaterial({ size: this.#pointDimensions.size, vertexColors: true });
		return new Points(geometry, material);
	}
}
